import { useState } from 'react';
import { Info, X } from 'lucide-react';

interface Location {
  lat: number;
  lon: number;
  name: string;
}

interface CustomCoordinateInputProps {
  onSubmit: (location: Location) => void;
  onCancel: () => void;
}

export function CustomCoordinateInput({ onSubmit, onCancel }: CustomCoordinateInputProps) {
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<{ lat?: string; lon?: string; name?: string }>({});

  const validateCoordinates = () => {
    const newErrors: { lat?: string; lon?: string; name?: string } = {};

    // Validate latitude
    const lat = parseFloat(latitude);
    if (!latitude.trim()) {
      newErrors.lat = 'Latitude is required';
    } else if (isNaN(lat)) {
      newErrors.lat = 'Latitude must be a valid decimal number';
    } else if (lat < -90 || lat > 90) {
      newErrors.lat = 'Latitude must be between -90 and 90';
    }

    // Validate longitude
    const lon = parseFloat(longitude);
    if (!longitude.trim()) {
      newErrors.lon = 'Longitude is required';
    } else if (isNaN(lon)) {
      newErrors.lon = 'Longitude must be a valid decimal number';
    } else if (lon < -180 || lon > 180) {
      newErrors.lon = 'Longitude must be between -180 and 180';
    }

    // Validate name
    if (!name.trim()) {
      newErrors.name = 'Location name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateCoordinates()) {
      onSubmit({
        lat: parseFloat(latitude),
        lon: parseFloat(longitude),
        name: name.trim(),
      });
    }
  };

  return (
    <div className="absolute inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 rounded-xl">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Custom Coordinates
          </h3>
          <button
            onClick={onCancel}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Info Section */}
            <div className="flex items-start gap-2 p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
              <Info className="h-4 w-4 text-pink-600 dark:text-pink-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-pink-700 dark:text-pink-300">
                <p className="font-medium mb-1">Coordinate Format:</p>
                <p>• Latitude: -90 to 90 (e.g., 47.3769)</p>
                <p>• Longitude: -180 to 180 (e.g., 8.5417)</p>
                <p>• Use decimal degrees format</p>
              </div>
            </div>

            {/* Latitude Input */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Latitude
              </label>
              <input
                type="text"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="e.g., 47.3769"
                className={`w-full px-3 py-2 bg-white dark:bg-gray-700 border rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-pink-500/20 transition-colors ${
                  errors.lat
                    ? 'border-red-300 dark:border-red-600 focus:border-red-300 dark:focus:border-red-500'
                    : 'border-gray-200 dark:border-gray-600 focus:border-pink-300 dark:focus:border-pink-500'
                }`}
              />
              {errors.lat && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.lat}</p>
              )}
            </div>

            {/* Longitude Input */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Longitude
              </label>
              <input
                type="text"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="e.g., 8.5417"
                className={`w-full px-3 py-2 bg-white dark:bg-gray-700 border rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-pink-500/20 transition-colors ${
                  errors.lon
                    ? 'border-red-300 dark:border-red-600 focus:border-red-300 dark:focus:border-red-500'
                    : 'border-gray-200 dark:border-gray-600 focus:border-pink-300 dark:focus:border-pink-500'
                }`}
              />
              {errors.lon && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.lon}</p>
              )}
            </div>

            {/* Name Input */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Location name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., My custom location"
                className={`w-full px-3 py-2 bg-white dark:bg-gray-700 border rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-pink-500/20 transition-colors ${
                  errors.name
                    ? 'border-red-300 dark:border-red-600 focus:border-red-300 dark:focus:border-red-500'
                    : 'border-gray-200 dark:border-gray-600 focus:border-pink-300 dark:focus:border-pink-500'
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.name}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onCancel}
                className="px-3 py-2 text-sm font-medium rounded-lg border transition-colors bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-2 text-sm font-medium rounded-lg transition-colors text-white bg-[#E34290] hover:bg-[#C93B82] focus:bg-[#C93B82]"
              >
                Add Location
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
